import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { pipefyApi } from "@/lib/api-client";

export default function TestQueryPage() {
  const [tableId, setTableId] = useState("306546622");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  
  // IDs pré-configurados
  const centrosCustoId = "306546622";
  const tiposDespesaId = "306808314";

  const handleTest = async () => {
    if (!tableId) {
      toast.error("Por favor, informe o ID da tabela");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const data = await pipefyApi.testQuery({ tableId: tableId.trim() });

      setResult(data);
      toast.success("Query executada com sucesso!");
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || "Erro ao fazer query no Pipefy");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 p-6 max-w-6xl w-full mx-auto">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Testar Query Pipefy</h2>
        <p className="text-muted-foreground text-sm">
          Teste a query GraphQL para ver a estrutura dos dados do database.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Query de Teste</CardTitle>
          <CardDescription>
            Informe o ID da tabela (database) do Pipefy para ver a estrutura dos dados
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="table-id">ID da Tabela (Database)</Label>
            <Input
              id="table-id"
              placeholder="Ex: 306546622"
              value={tableId}
              onChange={(e) => setTableId(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setTableId(centrosCustoId)}
              disabled={loading}
              className="flex-1"
            >
              Centros de Custo
            </Button>
            <Button
              variant="outline"
              onClick={() => setTableId(tiposDespesaId)}
              disabled={loading}
              className="flex-1"
            >
              Tipos de Despesa
            </Button>
          </div>

          <Button
            onClick={handleTest}
            disabled={loading || !tableId}
            className="w-full"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Executando..." : "Executar Query"}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <div className="space-y-4">
          {/* Campos disponíveis */}
          {result.allFields && result.allFields.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Campos Disponíveis (Primeiro Registro)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {result.allFields.map((field: any, idx: number) => (
                    <div key={idx} className="p-2 rounded border">
                      <div className="font-semibold">{field.name}</div>
                      <div className="text-sm text-muted-foreground">
                        Valor: {String(field.value || "(vazio)")}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Tipo: {field.valueType}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Registro completo */}
          {result.sampleRecord && (
            <Card>
              <CardHeader>
                <CardTitle>Registro Completo (Primeiro)</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded overflow-auto text-xs">
                  {JSON.stringify(result.sampleRecord, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Dados completos */}
          {result.data && (
            <Card>
              <CardHeader>
                <CardTitle>Resposta Completa da API</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded overflow-auto text-xs max-h-96">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

